import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import NotFound from "@/pages/NotFound";
import { Redirect, Route, Switch, useLocation } from "wouter";
import ErrorBoundary from "./components/ErrorBoundary";
import { GlobalLanguageSwitcher } from "./components/LanguageSwitcher";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LocaleSync } from "./i18n/useLocale";

// Eager pages — нужны для первой отрисовки (лендинг / вход / регистрация / 404)
import Home from "./pages/Home";
import Login from "./pages/Login";
import Register from "./pages/Register";

import { lazy, Suspense, useEffect } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { initPushNotifications, isNativeApp } from "@/lib/pushNotifications";

// Lazy pages — грузятся отдельными чанками по требованию
const AcceptHandover = lazy(() => import("./pages/AcceptHandover"));
const UploadByLink = lazy(() => import("./pages/UploadByLink"));
const PublicMusicStatus = lazy(() => import("./pages/PublicMusicStatus"));
const HomePreview = lazy(() => import("./pages/HomePreview"));
const AcceptCoachHandover = lazy(() => import("./pages/AcceptCoachHandover"));
const Tournaments = lazy(() => import("./pages/Tournaments"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CreateTournament = lazy(() => import("./pages/CreateTournament"));
const LiveScoreboard = lazy(() => import("./pages/LiveScoreboard"));
const TournamentDetail = lazy(() => import("./pages/TournamentDetail"));
const TournamentAdmin = lazy(() => import("./pages/TournamentAdmin"));
const AthleteDocuments = lazy(() => import("./pages/AthleteDocuments"));
const AdminPanel = lazy(() => import("./pages/admin"));
const OrganizerApplications = lazy(
  () => import("./pages/OrganizerApplications"),
);
const SchoolDashboard = lazy(() => import("./pages/school/SchoolDashboard"));
const SchoolCompetitions = lazy(
  () => import("./pages/school/SchoolCompetitions"),
);
const SchoolCompetitionDetail = lazy(
  () => import("./pages/school/SchoolCompetitionDetail"),
);
const SchoolStats = lazy(() => import("./pages/school/SchoolStats"));
const SchoolAthletes = lazy(() => import("./pages/school/SchoolAthletes"));
const SchoolAthleteView = lazy(
  () => import("./pages/school/SchoolAthleteView"),
);
const SchoolCoaches = lazy(() => import("./pages/school/SchoolCoaches"));
const SchoolTeams = lazy(() => import("./pages/school/SchoolTeams"));
const OrganizerCompetitions = lazy(
  () => import("./pages/organizer/OrganizerCompetitions"),
);
const OrganizerProfile = lazy(
  () => import("./pages/organizer/OrganizerProfile"),
);
const OrganizerStats = lazy(() => import("./pages/organizer/OrganizerStats"));
const OrganizerSubscription = lazy(
  () => import("./pages/organizer/OrganizerSubscription"),
);
const OrganizerCompetitionForm = lazy(
  () => import("./pages/organizer/OrganizerCompetitionForm"),
);
const OrganizerCompetitionDetail = lazy(
  () => import("./pages/organizer/OrganizerCompetitionDetail"),
);
const TournamentDraw = lazy(() => import("./pages/TournamentDraw"));
const StartProtocol = lazy(() => import("./pages/StartProtocol"));
const ScheduleManagement = lazy(
  () => import("./pages/organizer/ScheduleManagement"),
);
const ProtocolConstructor = lazy(
  () => import("./pages/organizer/ProtocolConstructor"),
);
const JudgingPage = lazy(() => import("./pages/organizer/Judging"));
const PublicSchedule = lazy(() => import("./pages/PublicSchedule"));
const AthleteCabinet = lazy(() => import("./pages/AthleteCabinet"));
const CoachCabinet = lazy(() => import("./pages/CoachCabinet"));
const CoachAthleteView = lazy(() => import("./pages/CoachAthleteView"));
const CoachCompetitions = lazy(() => import("./pages/coach/CoachCompetitions"));
const CoachCompetitionDetail = lazy(
  () => import("./pages/coach/CoachCompetitionDetail"),
);
const CoachCompetitionMobileTest = lazy(
  () => import("./pages/coach/CoachCompetitionMobileTest"),
);
const CoachApplicationDetail = lazy(
  () => import("./pages/coach/CoachApplicationDetail"),
);
// named export
const JudgeHubPage = lazy(() => import("./pages/judge-tablet/JudgeHubPage"));
const JudgeTabletPage = lazy(() =>
  import("./pages/judge-tablet").then((m) => ({ default: m.JudgeTabletPage })),
);
const LedDisplay = lazy(() => import("./pages/judge-tablet/LedDisplay"));
const WarmupBoard = lazy(() => import("./pages/WarmupBoard"));
const OfflinePult = lazy(() => import("./pages/OfflinePult"));
const PerformerDisplay = lazy(
  () => import("./pages/judge-tablet/PerformerDisplay"),
);
const SupportPage = lazy(() => import("./pages/SupportPage"));
const DemoPanels = lazy(() => import("./pages/DemoPanels"));
const KnowledgeBase = lazy(() => import("./pages/KnowledgeBase"));
const VideoGuides = lazy(() => import("./pages/VideoGuides"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Privacy = lazy(() => import("./pages/Privacy"));
const StreamOverlay = lazy(() => import("./pages/StreamOverlay"));
const SecretaryPanelConcept = lazy(
  () => import("./pages/SecretaryPanelConcept"),
);
const ChiefJudgePanelConcept = lazy(
  () => import("./pages/ChiefJudgePanelConcept"),
);

const RedirectSchoolApplications = () => <Redirect to="/school/competitions" />;
const RedirectOrganizerDashboard = () => <Redirect to="/organizer/profile" />;
const RedirectOrganizerCompetitions = () => (
  <Redirect to="/organizer/competitions" />
);

/** Лёгкий фолбэк для Suspense: без импортов из UI-библиотек */
function RouteFallback() {
  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-background">
      <div
        className="h-10 w-10 animate-spin rounded-full"
        style={{
          border: "3px solid rgba(139, 92, 246, 0.2)",
          borderTopColor: "#8b5cf6",
        }}
        role="status"
        aria-label="Loading"
      />
    </div>
  );
}

// Любой переход открывает страницу сверху. Раньше позиция прокрутки
// переносилась с предыдущего экрана — публичные страницы открывались «внизу».
function ScrollToTop() {
  const [path] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [path]);
  return null;
}

// Судейские планшеты, LED-табло, трансляции и живое табло остаются тёмными:
// в зале светлый экран слепит и мешает работать. Вешаем .theme-dark на <html>
// по маршруту — одна точка вместо правки каждого экрана.
// /demo переключается сам: витрина светлая, открытая панель — тёмная (см. DemoPanels)
const DARK_ROUTES = [/^\/j\//, /^\/display/, /^\/warmup/, /^\/live\//, /^\/overlay\//, /^\/performer/, /concept$/];
// Наложение для OBS — единственный экран, который должен быть ПРОЗРАЧНЫМ:
// браузерный источник рисует фон страницы поверх видео, и тёмный body ложился
// на картинку прямоугольником. Раньше это лечили CSS вручную в настройках OBS.
const TRANSPARENT_ROUTES = [/^\/overlay\//];
function ThemeScope() {
  const [path] = useLocation();
  useEffect(() => {
    const dark = DARK_ROUTES.some(re => re.test(path));
    document.documentElement.classList.toggle("theme-dark", dark);
    document.documentElement.classList.toggle(
      "overlay-transparent",
      TRANSPARENT_ROUTES.some(re => re.test(path)),
    );
    // Цвет строки состояния на телефоне: светлый фон платформы или тёмный зал
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", dark ? "#1B1B22" : "#F5F2FC");
    return () => {
      document.documentElement.classList.remove("theme-dark");
      document.documentElement.classList.remove("overlay-transparent");
    };
  }, [path]);
  return null;
}

function Router() {
  return (
    <Suspense fallback={<RouteFallback />}>
      <ScrollToTop />
      <ThemeScope />
      <Switch>
        {/* Public Pages */}
        <Route path="/home-preview" component={HomePreview} />
        <Route path="/" component={Home} />
        <Route path="/login" component={Login} />
        <Route path="/privacy" component={Privacy} />
        <Route path="/forgot-password" component={ForgotPassword} />
        <Route path="/reset-password" component={ResetPassword} />
        <Route path="/demo" component={DemoPanels} />
        <Route path="/secretary-concept" component={SecretaryPanelConcept} />
        <Route path="/chief-judge-concept" component={ChiefJudgePanelConcept} />
        <Route path="/tournaments" component={Tournaments} />
        <Route path="/live/:tournamentId" component={LiveScoreboard} />
        <Route path="/tournament/:id" component={TournamentDetail} />
        {/* Красивые адреса дизайнерских лендингов турниров */}
        <Route path="/gracecup">{() => <Redirect to="/tournament/27" />}</Route>
        <Route path="/almacup">{() => <Redirect to="/tournament/43" />}</Route>

        {/* Tournament Admin */}
        <Route
          path="/admin/tournament/:tournamentId"
          component={TournamentAdmin}
        />
        <Route
          path="/admin/tournament/:tournamentId/:section"
          component={TournamentAdmin}
        />
        <Route
          path="/admin/tournament/:tournamentId/draw"
          component={TournamentDraw}
        />

        {/* Start Protocols (public) */}
        <Route
          path="/tournament/:tournamentId/protocol"
          component={StartProtocol}
        />

        {/* Stream overlay for OBS */}
        <Route path="/overlay/:competitionId" component={StreamOverlay} />

        {/* Public Schedule */}
        <Route path="/schedule/:tournamentId" component={PublicSchedule} />

        {/* Registration */}
        <Route path="/register" component={Register} />
        <Route path="/register/school" component={Register} />
        <Route path="/register/organizer" component={Register} />
        <Route path="/accept-invite/:token" component={AcceptHandover} />
        {/* Загрузка фото и музыки родителем по ссылке тренера — без входа */}
        <Route path="/upload/:token" component={UploadByLink} />
        {/* Публичный отчёт «Не сданная музыка» — по ссылке, без входа. */}
        <Route path="/music/:id" component={PublicMusicStatus} />
        <Route
          path="/accept-coach-invite/:token"
          component={AcceptCoachHandover}
        />

        {/* Dashboard */}
        <Route path="/school/profile" component={SchoolDashboard} />
        <Route path="/school/athletes" component={SchoolAthletes} />
        <Route
          path="/school/athletes/:athleteId"
          component={SchoolAthleteView}
        />
        <Route
          path="/school/athletes/:athleteId/:section"
          component={SchoolAthleteView}
        />
        <Route path="/school/competitions" component={SchoolCompetitions} />
        <Route
          path="/school/competitions/:id"
          component={SchoolCompetitionDetail}
        />
        <Route
          path="/school/applications"
          component={RedirectSchoolApplications}
        />
        <Route
          path="/school/applications/:id"
          component={RedirectSchoolApplications}
        />
        <Route path="/school/coaches" component={SchoolCoaches} />
        <Route path="/school/teams" component={SchoolTeams} />
        <Route path="/school/stats" component={SchoolStats} />
        <Route
          path="/dashboard/organizer"
          component={RedirectOrganizerDashboard}
        />
        <Route
          path="/dashboard/organizer/competitions"
          component={RedirectOrganizerCompetitions}
        />
        <Route path="/organizer/profile" component={OrganizerProfile} />
        <Route
          path="/organizer/competitions/new"
          component={OrganizerCompetitionForm}
        />
        <Route
          path="/organizer/competitions/:id/edit"
          component={OrganizerCompetitionForm}
        />
        <Route
          path="/organizer/competitions/:id/protocol"
          component={ProtocolConstructor}
        />
        <Route
          path="/organizer/competitions/:id/judging"
          component={JudgingPage}
        />
        <Route
          path="/organizer/competitions/:id"
          component={OrganizerCompetitionDetail}
        />
        <Route
          path="/organizer/competitions"
          component={OrganizerCompetitions}
        />
        <Route
          path="/organizer/subscription"
          component={OrganizerSubscription}
        />
        <Route path="/organizer/stats" component={OrganizerStats} />
        <Route path="/dashboard/tournaments/new" component={CreateTournament} />
        <Route path="/dashboard/:section" component={Dashboard} />
        <Route path="/dashboard" component={Dashboard} />

        {/* Athlete cabinet */}
        <Route path="/athlete" component={AthleteCabinet} />
        <Route path="/athlete/:section" component={AthleteCabinet} />
        <Route path="/athlete/:id/documents" component={AthleteDocuments} />

        {/* Coach cabinet */}
        {/* Hidden, coach-only prototype. Intentionally not linked from navigation. */}
        <Route
          path="/coach/mobile-competition-test/:id"
          component={CoachCompetitionMobileTest}
        />
        <Route
          path="/coach/competitions/:id"
          component={CoachCompetitionDetail}
        />
        <Route path="/coach/competitions" component={CoachCompetitions} />
        <Route
          path="/coach/applications/:id"
          component={CoachApplicationDetail}
        />
        <Route path="/coach" component={CoachCabinet} />
        <Route path="/coach/:section" component={CoachCabinet} />
        <Route path="/coach/athletes/:athleteId" component={CoachAthleteView} />
        <Route
          path="/coach/athletes/:athleteId/:section"
          component={CoachAthleteView}
        />

        {/* Organizer Applications */}
        <Route
          path="/organizer/applications"
          component={OrganizerApplications}
        />

        {/* Organizer Schedule Management */}
        <Route
          path="/organizer/schedule/:tournamentId"
          component={ScheduleManagement}
        />
        <Route
          path="/organizer/draw/:tournamentId"
          component={TournamentDraw}
        />

        {/* Support & Knowledge Base */}
        <Route path="/support" component={SupportPage} />
        <Route path="/knowledge" component={KnowledgeBase} />
        <Route path="/video" component={VideoGuides} />

        {/* Admin Panel */}
        <Route path="/admin" component={AdminPanel} />
        <Route path="/admin/:section" component={AdminPanel} />

        {/* Judge Tablet (by access code) */}
        <Route path="/j/panel/:hubCode" component={JudgeHubPage} />
        <Route path="/j/:code" component={JudgeTabletPage} />

        {/* LED Display (for scoreboard) */}
        <Route path="/display">{() => <LedDisplay />}</Route>
        <Route path="/warmup">{() => <WarmupBoard />}</Route>
        <Route path="/offline">{() => <OfflinePult />}</Route>

        {/* Scores-only display (экран оценок): LED в режиме только оценок */}
        <Route path="/scores">{() => <LedDisplay scoresOnly />}</Route>

        {/* Performer info display (LED with current athlete: line/rect/portrait/square) */}
        <Route path="/performer/:competitionId" component={PerformerDisplay} />

        {/* 404 */}
        <Route path="/404" component={NotFound} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function PushInit() {
  const { user, isAuthenticated } = useAuth();
  const registerTokenMutation = trpc.push.registerToken.useMutation();

  useEffect(() => {
    if (!isAuthenticated || !user || !isNativeApp()) return;
    initPushNotifications((token, platform) => {
      registerTokenMutation.mutate({ token, platform });
    });
  }, [isAuthenticated, user]);

  return null;
}

function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="dark">
        <TooltipProvider>
          <Toaster />
          <PushInit />
          <LocaleSync />
          <GlobalLanguageSwitcher />
          <Router />
        </TooltipProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
